import { WorkspaceSwitch } from "./WorkspaceSwitch";
import React, { useState } from "react";
import { useSite } from "../context/SiteContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import NavOverflow from "./NavOverflow";
import { useFeatures } from "../context/FeatureContext";
import { useCart } from "../pages/pharmacy/PharmacyStore";
import { ShoppingCart } from "lucide-react";
import { 
  Phone, 
  MapPin, 
  Search, 
  Calendar, 
  User, 
  LogOut, 
  ShieldCheck, 
  Heart, 
  Menu, 
  X,
  FileText,
  Clock,
  Sparkles,
  Stethoscope
} from "lucide-react";

export default function Navbar({ onOpenSearch, onBookClick }) {
  const { user, logout } = useAuth();
  const { count: cartCount } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const { settings: site, nav, announcements } = useSite();
  const s = site || {};
  const hdr = s.header || {};
  const { hiddenPath } = useFeatures();
  const visible = (nav || []).filter((n) => !hiddenPath(n.url || ""));
  const topItems = visible.filter((n) => !n.parent_id);
  const childrenOf = (id) => visible.filter((n) => n.parent_id === id);
  const ann = (announcements || []).filter((a) => a.placement !== "banner")[0];
  const NavItem = ({ n, mobile }) => {
    const kids = childrenOf(n.id);
    const cls = mobile ? "block py-1.5 text-[13px] font-medium text-gray-800" : `hover:text-[#0D3B2E] transition-colors py-1 ${isActive(n.url) ? 'text-[#0D3B2E] font-semibold border-b-2 border-[#0D3B2E]' : ''} ${n.highlight ? 'text-rose-700 font-semibold' : ''}`;
    const inner = /^(https?:|tel:|mailto:)/.test(n.url || "") ? <a href={n.url} className={cls} data-testid={`nav-link-${n.id}`} target="_blank" rel="noreferrer">{n.label}</a> : <Link to={n.url || "/"} className={cls} data-testid={`nav-link-${n.id}`} onClick={() => mobile && setMobileMenuOpen && setMobileMenuOpen(false)}>{n.label}</Link>;
    if (!kids.length) return inner;
    return (
      <div className={mobile ? "" : "relative group"}>{inner}
        <div className={mobile ? "pl-4" : "absolute left-0 top-full pt-2 hidden group-hover:block z-50"}>
          <div className={mobile ? "" : "bg-white rounded-2xl shadow-xl border border-gray-100 p-2 min-w-[200px]"}>{kids.map((k) => <Link key={k.id} to={k.url || "/"} data-testid={`nav-link-${k.id}`} onClick={() => mobile && setMobileMenuOpen(false)} className={`block px-3 ${mobile ? "py-1" : "py-2"} text-xs rounded-xl hover:bg-[#EBF4F1] text-gray-800`}>{k.label}</Link>)}</div>
        </div>
      </div>
    );
  };
  if (hdr.visible === false) return null;
  return (
    <header className={`${hdr.sticky === false ? "relative" : "sticky top-0"} z-40 w-full shadow-sm`} data-testid="site-header">
      {ann && <div className="text-[#07221A] text-xs py-1.5 px-4 text-center font-semibold" style={{ background: ann.color || "#F5C451" }} data-testid="announcement-bar">{ann.title}{ann.body ? ` — ${ann.body}` : ""} {ann.cta_label && <Link to={ann.cta_url || "/"} className="underline ml-1">{ann.cta_label}</Link>}</div>}
      {hdr.announcement?.active && hdr.announcement.text && <div className="text-white text-xs py-1.5 px-4 text-center font-semibold" style={{ background: hdr.announcement.bg || "#F59E0B" }} data-testid="header-announcement">{hdr.announcement.text} {hdr.announcement.link_label && <Link to={hdr.announcement.link_url || "/"} className="underline ml-1">{hdr.announcement.link_label}</Link>}</div>}
      {/* Emergency & Top Bar */}
      <div className="bg-[#07221A] text-[#E8F1EE] text-xs py-1.5 px-4 sm:px-8 border-b border-[#165644]/40 flex flex-wrap justify-between items-center">
        <div className="flex items-center space-x-4 sm:space-x-6">
          <span className="flex items-center gap-1.5 text-emerald-300 font-medium">
            <MapPin className="w-3.5 h-3.5 text-[#C59B27]" />
            <span data-testid="topbar-address">{s.address_short}</span>
          </span>
          <span className="hidden md:inline-flex items-center gap-1.5 text-gray-300">
            <Clock className="w-3.5 h-3.5 text-emerald-400" />
            <span data-testid="topbar-hours">{s.opening_hours}</span>
          </span>
        </div>
        <div className="flex items-center space-x-4">
          {hdr.show_phone !== false && <a 
            href={`tel:${s.primary_phone}`} 
            data-testid="navbar-emergency-helpline"
            className="flex items-center gap-1.5 text-amber-300 font-semibold hover:text-amber-200 transition-colors"
          >
            <Phone className="w-3 h-3" />
            <span data-testid="topbar-phone">Helpline: {s.primary_phone}</span>
          </a>}
          {hdr.badge_text && <span className="hidden sm:inline-block text-xs bg-emerald-900/60 text-emerald-200 px-2 py-0.5 rounded border border-emerald-700/50" data-testid="topbar-badge">
            {hdr.badge_text}
          </span>}
        </div>
      </div>

      {/* Main Navigation Header */}
      <nav className="glass-header border-b border-[#0D3B2E]/10 px-3 sm:px-8 py-3.5">
        <div className="max-w-[1500px] mx-auto flex items-center justify-between gap-2 sm:gap-4">
          
          {/* Brand Logo */}
          <Link to={s.logo_link || "/"} data-testid="navbar-brand-logo" className="flex items-center gap-2 sm:gap-3 group shrink-0">
            <img src={s.logo_url || "/logo.png"} alt={`${s.short_name || "6SigmaCare"} logo`} data-testid="navbar-brand-logo-img" className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl shadow-md border border-emerald-700/40 group-hover:scale-105 transition-transform shrink-0 object-cover" />
            <div className="min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="font-serif-luxury text-xl sm:text-2xl tracking-tight text-[#0D3B2E] font-bold" data-testid="navbar-brand-name">
                  {s.short_name || "6SigmaCare"}
                </span>
                <span className="hidden sm:inline xl:hidden 2xl:inline text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-800 px-1.5 py-0.2 rounded">
                  Polyclinic
                </span>
              </div>
              <p className="hidden sm:block xl:hidden text-[11px] text-[#4E6159] tracking-wide font-medium truncate">
                Integrated OPD & Advanced Diagnostics • Kolkata
              </p>
            </div>
          </Link>

          {/* Desktop Search Bar Trigger */}
          <button
            onClick={onOpenSearch}
            type="button" data-testid="navbar-omni-search-button"
            className="hidden lg:flex items-center gap-3 px-4 py-2 bg-white/90 hover:bg-white text-gray-500 rounded-full border border-gray-200 shadow-sm hover:shadow transition-all w-72 xl:w-10 xl:h-10 xl:px-0 xl:justify-center shrink-0 text-left"
            title="Search doctor, test, package"
          >
            <Search className="w-4 h-4 text-[#3B8271]" />
            <span className="text-xs text-gray-500 flex-1 truncate xl:hidden">Search doctor, test, package...</span>
            <kbd className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded font-mono xl:hidden">⌘K</kbd>
          </button>

          {(!user || user.role === "patient") && (
            <Link to="/medicines/cart" data-testid="navbar-cart-link" aria-label={`Medicine cart${cartCount ? `, ${cartCount} item${cartCount === 1 ? "" : "s"}` : ""}`} className="relative w-10 h-10 shrink-0 rounded-full border border-gray-200 bg-white/90 hover:bg-white flex items-center justify-center text-[#0D3B2E]">
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && <span data-testid="navbar-cart-count" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-amber-400 text-[#07221A] text-[10px] font-bold flex items-center justify-center">{cartCount}</span>}
            </Link>
          )}

          {/* Nav Links */}
          <NavOverflow items={topItems} className="text-[13px] font-medium text-[#192621]" renderItem={(n) => <NavItem n={n} />} renderMenuItem={(n) => <div className="px-3 py-1 rounded-xl hover:bg-[#EBF4F1] text-xs"><NavItem n={n} mobile /></div>} />

          {/* Action CTAs */}
          <div className="flex items-center space-x-1.5 sm:space-x-3 shrink-0">
            {/* Mobile Search Button */}
            <button
              onClick={onOpenSearch}
              type="button" data-testid="navbar-mobile-search-trigger"
              className="lg:hidden p-2 text-[#0D3B2E] bg-white rounded-full border border-gray-200 shadow-sm"
              title="Search"
            >
              <Search className="w-4 h-4" />
            </button>

            {/* Quick Book Appointment CTA - hidden below sm to preserve mobile space */}
            {hdr.cta_visible !== false && <button
              onClick={() => hdr.cta_url && hdr.cta_url !== "/doctors" ? (window.location.href = hdr.cta_url) : onBookClick()}
              type="button" data-testid="navbar-book-appointment-cta"
              className="hidden md:flex items-center gap-2 px-3 lg:px-4 py-2 rounded-full bg-[#0D3B2E] hover:bg-[#165644] text-white text-xs font-semibold tracking-wide shadow-sm hover:shadow transition-all whitespace-nowrap"
            >
              <Calendar className="w-3.5 h-3.5 text-amber-300" />
              <span className="hidden lg:inline">{hdr.cta_label || "Book Appointment"}</span>
              <span className="lg:hidden">Book</span>
            </button>}

            {/* User Account / Login */}
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  data-testid="navbar-user-profile-menu"
                  className="flex items-center gap-2 px-3 py-1.5 bg-[#EBF4F1] border border-[#3B8271]/30 rounded-full text-xs font-semibold text-[#0D3B2E] hover:bg-emerald-100 transition-colors"
                >
                  {user.avatar_url ? <img src={user.avatar_url.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user.avatar_url} alt="" data-testid="navbar-user-avatar" className="w-6 h-6 rounded-full object-cover border border-[#0D3B2E]/20" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="w-6 h-6 rounded-full bg-[#0D3B2E] text-amber-300 flex items-center justify-center text-xs font-bold">
                    {user.name ? user.name[0].toUpperCase() : "U"}
                  </div>}
                  <span className="max-w-[90px] truncate hidden md:inline xl:hidden 2xl:inline">{user.name.split(" ")[0]}</span>
                  <span className="hidden 2xl:inline text-[10px] bg-emerald-800 text-white px-1.5 py-0.2 rounded font-mono uppercase max-w-[90px] truncate">
                    {user.role.replace("_", " ")}
                  </span>
                </button>

                {userDropdownOpen && (
                  <div 
                    data-testid="navbar-user-dropdown-popup"
                    className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 py-2 z-50 animate-in fade-in slide-in-from-top-2"
                  >
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-xs font-bold text-gray-800 truncate">{user.name}</p>
                      <p className="text-[11px] text-gray-500 truncate">{user.email}</p>
                    </div>

                    {user.role === "patient" && (
                    <Link
                      to="/account/dashboard"
                      onClick={() => setUserDropdownOpen(false)}
                      data-testid="nav-dropdown-my-health"
                      className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-[#EBF4F1] hover:text-[#0D3B2E]"
                    >
                      <FileText className="w-4 h-4 text-[#3B8271]" />
                      My Health Dashboard & Reports
                    </Link>
                    )}

                    {user.corporate_id && (
                      <Link
                        to="/corporate/portal/legacy"
                        onClick={() => setUserDropdownOpen(false)}
                        data-testid="nav-dropdown-corporate-portal"
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-[#0D3B2E] bg-[#EBF4F1] hover:bg-emerald-100"
                      >
                        <ShieldCheck className="w-4 h-4 text-[#3B8271]" />
                        Corporate HR Portal
                      </Link>
                    )}

                    <WorkspaceSwitch onDone={() => setUserDropdownOpen(false)} className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-bold text-[#0D3B2E] hover:bg-[#EBF4F1] text-left" />
                    {user.role === "patient" && !(user.roles || []).includes("doctor") && (
                      <Link
                        to="/doctors/join"
                        onClick={() => setUserDropdownOpen(false)}
                        data-testid="nav-dropdown-doctor-join"
                        className="flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-gray-700 hover:bg-[#EBF4F1] hover:text-[#0D3B2E]"
                      >
                        <Stethoscope className="w-4 h-4 text-[#3B8271]" />
                        {(user.roles || []).includes("doctor_applicant") ? "My Doctor Application" : "Join as a Doctor"}
                      </Link>
                    )}
                    <button
                      onClick={() => {
                        setUserDropdownOpen(false);
                        logout();
                      }}
                      data-testid="nav-dropdown-logout-btn"
                      className="w-full flex items-center gap-2.5 px-4 py-2 text-xs font-medium text-rose-600 hover:bg-rose-50 text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate("/login")}
                type="button" data-testid="navbar-signin-button"
                className="flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 rounded-full border border-[#0D3B2E] text-[#0D3B2E] hover:bg-[#0D3B2E] hover:text-white text-xs font-semibold transition-all whitespace-nowrap"
              >
                <User className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sign In</span>
              </button>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              type="button" data-testid="navbar-mobile-menu-toggle"
              className="xl:hidden p-2 text-gray-700 hover:text-[#0D3B2E]"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Nav Menu — compact, scrolls within the viewport, CTA always visible above the bottom nav */}
        {mobileMenuOpen && (
          <div data-testid="mobile-navigation-drawer" className="xl:hidden border-t border-gray-100 mt-2 flex flex-col max-h-[calc(100dvh-15.5rem)] min-h-0">
            <div className="overflow-y-auto pt-1.5 pb-1 space-y-0 min-h-0">
              {topItems.map((n) => <div key={n.id} className="px-3" data-testid={`mobile-nav-${n.id}`}><NavItem n={n} mobile /></div>)}
            </div>
            <div className="shrink-0 pt-2 pb-2 px-1 bg-white">
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  onBookClick();
                }}
                data-testid="mobile-menu-book-cta"
                className="w-full py-2.5 rounded-xl bg-[#0D3B2E] text-white font-semibold text-xs flex items-center justify-center gap-2"
              >
                <Calendar className="w-4 h-4 text-amber-300" />
                Book Appointment
              </button>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}
